import React from "react";

import { Tooltip, tooltipClasses } from "@mui/material";
import { styled } from "@mui/material/styles";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Scrollbar, A11y, Keyboard, Mousewheel, EffectCreative, EffectCards } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import bg from "../../assets/images/articles/Archipel/archipel-13.jpg";
import img1 from "../../assets/images/articles/Archipel/archipel-01.jpg";
import img2 from "../../assets/images/articles/Archipel/archipel-08.jpg";
import img3 from "../../assets/images/articles/Archipel/archipel-07.jpg";
import img4 from "../../assets/images/articles/Archipel/archipel-12.jpg";
import img5 from "../../assets/images/articles/Archipel/archipel-10.jpg";
import img6 from "../../assets/images/articles/Archipel/archipel-14.jpg";

import slide1_1 from "../../assets/images/articles/Archipel/archipel-05.jpg";
import slide1_2 from "../../assets/images/articles/Archipel/archipel-06.jpg";
import slide1_3 from "../../assets/images/articles/Archipel/archipel-09.jpg";
import slide1_4 from "../../assets/images/articles/Archipel/archipel-02.jpg";
import slide1_5 from "../../assets/images/articles/Archipel/archipel-03.jpg";
import slide1_6 from "../../assets/images/articles/Archipel/archipel-04.jpg";

import downloadArticle from "../../assets/pdf/article_Archipel-Workshop-Temporality_Otto-van-Sloun.pdf";



const HtmlTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(
  ({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "white",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 400,
      fontSize: theme.typography.pxToRem(12),
      border: "1px solid black",
      borderRadius: "1px",
    },
  })
);

function Archipel() {
  return (
    <div className="Archipel" id="background">
      <div className="Archipel__backgroundVideo">
        <div className="Archipel__backgroundVideo__wrapper">
          <div className="Archipel__backgroundVideo__title">
            <h1 className="editH1">Archipel Workshop: ‘Temporality’</h1>
            <h2 className="editH2">Otto van Sloun | 13.03.2025</h2>
          </div>
        </div>
        <img src={bg} className="Archipel_bg_img" alt="" />
      </div>

      <div className="Archipel__container">
        <div className="Archipel__container__title">
          <h1 className="editH1">
            Archipel Workshop: <br /> ‘Temporality’
          </h1>
          <h2 className="editH2">Otto van Sloun | 13.03.2025</h2>
        </div>

        <div className="Archipel__container__intro">
          <div className="Archipel__intro__img">
            <img src={img1} alt="" />
            <p className="credit__margin"> @Simon Breynaert </p>
          </div>

          <div className="Archipel__intro__text">
            How do we design for a moment, not a monument? What can architecture become when duration becomes the
            measure, not material? These were among the questions that shaped our contributions to the Archipel Workshop
            Day. Organized as part of a wider constellation of lectures, study days, and excursions, this particular day
            brought together six spatial practices and around 100 students. The task: to collaboratively create and
            present ephemeral interventions that explored <mark>temporariness as method</mark>, material, and message.{" "}
            <mark>“Time”</mark> here was our guide. Not as chronology, not as nostalgia, but as a living agent –
            unpredictable, shapeshifting, always present yet beyond control. Navigating ephemeral architectures and
            temporal practices, not to define them but to experience their edges. We came together – designers,
            thinkers, performers, and students – in a collective exploration of temporariness. Not as a placeholder or a
            pause, but as an intention. As a vision. As a method. We asked: What happens when we design with time, not
            against it, as a co-creator?
          </div>
        </div>

        <div className="Archipel__container__bigImg">
          <img className="Archipel__big__img" src={img2} alt="" />
          <p className="credit__margin"> @Simon Breynaert </p>
        </div>

        <div className="Archipel__container__body pad__top">
          <div className="Archipel__body__text">
            <div>
              <h2>Time as Actor, Not Archive</h2>
              Rather than historicizing space or imagining speculative futures, we engaged with time as an embedded
              presence, shaping both perception and production. This echoes architectural theorist Jeremy Till’s
              assertion in <i>Architecture Depends</i><HtmlTooltip
              title="Jeremy Till, Architecture Depends (MIT Press, 2009)"
              placement="bottom-start"
            >
              <sup className="tooltip_ref">1</sup>
            </HtmlTooltip> that architecture is “always contingent, always
              conditional, always entangled with the world.” Temporality, in this view, is not a disruption of design
              but its very condition. The workshop, held at the LUCA Arts campus garage in Ghent, unfolded as a
              durational practice itself – a series of cycles, unfolding over the afternoon and into the evening.
            </div>
            <div>
              <h2>To unlearn</h2>
              RAs Club Dérive, being a cross-disciplinary collective inspired by Situationist practice, we curated a
              workshop format that embraced time not only as a theme but also as a tactic. The dérive, as conceptualized
              by Guy Debord and the Situationist International<HtmlTooltip
              title="‘Club Dérive’, https://www.clubderive.com/about"
              placement="bottom-start"
            >
              <sup className="tooltip_ref">2</sup>
            </HtmlTooltip>, is defined as a tool to subvert dominant
              spatial logistics: a form of free-roaming through space, whilst documenting the effects of this space on
              the ‘user’/’participant’. No rulers or renderings, but bodies in motion. No blueprints, only traces. The
              body as both archive and activator. The city not as an object to be studied, but as a shifting terrain to
              drift through – blindfolded, crawling, balancing, breathing. The goal: to <mark>unlearn</mark>{" "}
              architectural reading and open up space for other forms of knowing. This unlearning is rooted in{" "}
              <i>exformation</i>, a term coined by Tor Nørretranders as “the explicitly discarded information” that
              paradoxically makes understanding possible and popularized in Japanese designer Kenya Hara’s book
              Ex-formation. We did not seek to inform the site, but to unlearn it, and thus understand it. We devided
              the experience into three interrelated phases:
              <div className="Archipel__body__text__middle__incline">
                <br /> 1. Exploration
                <br /> 2. Activation
                <br /> 3. Documentation
              </div>
            </div>
          </div>
          <div className="Archipel__body__image">
            <img src={img3} alt="" />
          </div>
        </div>

        <div className="Archipel__container__bigImg">
          <img className="Archipel__big__img" src={img4} alt="" />
          <p className="credit__margin"> @Simon Breynaert </p>
        </div>

        <div className="Archipel__container__body">
          <div className="Archipel__body__text__middle" id="get__H2">
            <h2>Body as a tool</h2>
            We rejected a hierarchical logic of exploration → activation → documentation. Instead, these phases existed
            as a continuous loop – or more aptly, a spiral. Not linearly, but rhythmically – a pulse: 1-3-2-3, returning
            again and again to the moment of witnessing. To explore a space was already to activate it. Every action,
            however minor, left a trace. And every trace, if attended to, became a document of potential. This aligns
            with the work of philosopher Elizabeth Grosz, who argues that architecture should not be seen as a static
            envelope, but as a dynamic “frame for bodies in motion.” Bodies do not simply move through space; they
            co-create it. Similar thoughts can be found in the discourse of spatial theory by figures such as Lefebvre
            or De Certeau, discussing the relationship and interaction between space, place and their activation through
            the presence of people. Dance theorist André Lepecki also emphasizes the politics of presence, suggesting
            that certain movements can be a way of resisting the logics of productivity and permanence that define late
            capitalist space. In this spirit, our workshop became a field of <mark>micro-resistances</mark>: gestures
            too subtle to be archived, too fleeting to be commercialized, yet powerful in their refusal to solidify.
            <br />
            <br />
            The workshop unfolded as a guided dérive through the site. Students explored and measured space not with
            tools, but with gestures. Rather than reducing space to visual representation, we aimed to sense it through
            touch, through proximity, resistance, friction. Elbows became yardsticks; footsteps mapped affective
            distances. If the exploration phase is about observing and responding intuitively, activation is about
            actively/consciously responding to these drifts playfully.
            <br />
            <br />
            We asked participants to:
            <div className="Archipel__body__text__middle__incline">
              <br />· Drift instinctively: blindfolded, guided by a partner…
              <br />· Trace paths of attraction through space
              <br />· Use chalk to reveal invisible lines of presence
              <br />· Discover “chill zones” – architectures of rest and softness
              <br />· Intervene in space by blocking, framing, or redirecting movement
              <br />· Activate sensory constraints
            </div>
            <br />
            <br />
            Here, the body became both cartographer and cartography, echoing Erin Manning’s work on “body-worlding” in
            <i>Relationscapes</i><HtmlTooltip
              title="Erin Manning, Relationscapes: Movement, Art, Philosophy, Technologies of Lived Abstraction (MIT Press, 2009)"
              placement="bottom-start"
            >
              <sup className="tooltip_ref">6</sup>
            </HtmlTooltip>, where movement itself generates knowledge. Inspired by Haus-Rucker-Co’s
            <i>provisional architecture</i> from the 1960s – where urban interventions were installed to provoke public
            reaction – we treated our site as an experimental ground for “trial and error.” This approach is an
            important principle in the provisional process of spatial design, as it gives an open end to the destiny of
            time based architectural interventions. But rather than spectacular urban spectacles, our interventions were
            subtle, almost invisible. A chalk line. A body curled in a corner. In her essay “The Temporality of the
            Postmodern,” scholar Linda Hutcheon reminds us that the postmodern slot is not defined by what it is, but by
            what it interrupts. Like provisional architecture, it deals with confrontation.Our workshop did not offer
            answers: it posed temporal interruptions: almost performative-like suspensions that made the familiar
            strange. Rather than producing artifacts, we collected <mark>traces</mark>. Photographs, sketches, videos,
            and embodied memories. The documentation was not about capture, but <mark>continuation</mark> – a way to
            extend the work beyond its moment without fossilizing it. Drawing on Jacques Derrida’s concept of <i>trace</i><HtmlTooltip
              title="Jacques Derrida, Of Grammatology, 16th pr (Johns Hopkins Univ. Pr, 1995)"
              placement="bottom-start"
            >
              <sup className="tooltip_ref">8</sup>
            </HtmlTooltip>, we understood every mark, every move, every pause as a residual inscription of
            presence. What disappears also leaves an echo – and in this, we find a new kind of archive. Not a library of
            things, but of individual experiences of the same space.
          </div>
        </div>

        <div className="Archipel__container__swiper">
          <div className="Archipel__container__swiper_s">
            <Swiper
              modules={[Navigation, Pagination]}
              spaceBetween={20}
              slidesPerView={3}
              loop={true}
              centeredSlides={true}
              keyboard={{
                enabled: true,
              }}
              mousewheel={true}
              grabCursor={true}
              navigation
              onSwiper={(swiper) => console.log(swiper)}
              onSlideChange={() => console.log("slide change")}
            >
              <SwiperSlide>
                <img src={slide1_1} alt="" />
              </SwiperSlide>
              <SwiperSlide>
                <img src={slide1_2} alt="" />
              </SwiperSlide>
              <SwiperSlide>
                <img src={slide1_5} alt="" />
              </SwiperSlide>
              <SwiperSlide>
                <img src={slide1_6} alt="" />
              </SwiperSlide>
              <SwiperSlide>
                <img src={slide1_3} alt="" />
              </SwiperSlide>
              <SwiperSlide>
                <img src={slide1_4} alt="" />
              </SwiperSlide>
            </Swiper>
          </div>
          <p className="credit__margin"> @Simon Breynaert </p>
        </div>

        <div className="Archipel__container__body pad__top">
          <div className="Archipel__body__text">
            <div>
              <h2>Metagraphy</h2>
              The study of the interactions between user and environment is what Situationists defined as
              <i>psychogeography</i>, and has defined to a large extent how Club Derive approaches its projects. Not
              only is the interaction between space and user a driving factor in our designs, within Club Derive we also
              try to look for ways of documenting this information in new formats. Our goal for the workshop was to
              introduce to the group of students alternative ways of mapping space, in order to observe the{" "}
              <i>psychogeography</i> of it. In accordance with the Situationist tactics, we gathered our experiences and
              observations in photos and drawings, and hung them on a large white wall in order to create our new map of
              the space. The Situationists called these maps <i>metagraphy</i> (at times also referred to as
              hypergraphy), because they carry not only structural information, but are layered with observations of
              lived experience, exformation, provisional situations etc.
            </div>
            <div>
              <h2>The Instant Expo</h2>
              The evening culminated in a one-night-only exhibition: a walking tour of the day’s interventions, some
              only shown or activated for a short, specific timeslot. Visitors had one – maybe two – chances to
              encounter the work. If they missed it, it was gone. This was by design. The exhibition was{" "}
              <mark>durational</mark>, not spatial. Each intervention became an exposition, structured by the very time
              it took place in. A moment, shared, then dissolved. This approach resonates with the curatorial
              methodologies of ‘time-based architecture’ and ‘performance architecture’, as discussed by Andrés Jaque
              and others, where the line between event and structure is purposefully blurred.
            </div>
          </div>
          <div className="Archipel__body__image">
            <img className="Archipel__body__image_06" src={img6} alt="" />
          </div>
        </div>

        <div className="Archipel__container__bigImg">
          <img className="Archipel__big__img" src={img5} alt="" />
          <p className="credit__margin"> @Simon Breynaert </p>
        </div>

        <div className="Archipel__container__body">
          <div className="Archipel__body__text__middle pad__top" id="get__H2">
            <h2>Concluding Thoughts</h2>
            The Archipel Workshop Day allowed us to inhabit a critical position toward architecture – one that accepts
            transience, celebrates process, and uses temporality not as a problem to be solved but as a lens through
            which space can be differently imagined. In this spirit, our intervention did not result in an object, but
            in a <mark>situation</mark>. One that foregrounded the now, asked us to dwell within it, and reminded us – as Henri
            Lefebvre wrote in <i>The Production of Space</i><HtmlTooltip
              title="Lefebvre en Nicholson-Smith, The Production of Space"
              placement="bottom-start"
            >
              <sup className="tooltip_ref">11</sup>
            </HtmlTooltip> – that “space is a social product.” And so is time.
            Club Dérive’s approach – part derive, part performing/measuring, part speculative cartography – offered a
            method for accessing the unseen and the felt. Temporality became a terrain. And time, as always, moved on.
          </div>
        </div>

        <div className="Archipel__container__body">
          <div className="Archipel__body__text__middle middle__center" id="get__H2">
            Thanks to Archipel for the invitation and all the participants.
            <br /><br /><br />
            Workshop facilitators: Loïc Tindemans, Fenia Proost & Otto van Sloun.
          </div>
        </div>

        <div className="Archipel__container__body">
          <div className="Archipel__body__text__middle middle__center" id="get__H2">
            <div className="display__flex">
              <div className="article__download__button">
                <a href={downloadArticle} download="article_Archipel-Workshop-Temporality_Otto-van-Sloun">
                  Download Article
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="Archipel__container__body">
            <div className='Archipel__body__text__middle' id='get__H2'>
              <h2>Sources</h2>
                <br /><br />Jeremy Till, Architecture Depends (MIT Press, 2009).
                <br /><br />‘Club Dérive’, https://www.clubderive.com/about.
                <br /><br />‘Exformation’, http://www.worldwidewords.org/tp-exf1.html.
                <br /><br />Hélène Frichot, ‘Elizabeth Grosz, Chaos, Territory, Art: Deleuze and the Framing of the Earth, Irvington, NY: Columbia University Press, 2008’, Architectural Theory Review 14, nr. 2 (2009), https://doi.org/10.1080/00779950902919583.
                <br /><br />Henri Lefebvre en Donald Nicholson-Smith, The Production of Space, Reprinted (Blackwell, 1997).
                <br /><br />Erin Manning, Relationscapes: Movement, Art, Philosophy, Technologies of Lived Abstraction (MIT Press, 2009).
                <br /><br />Linda Hutcheon, The Politics of Postmodernism: Parody and History, 1987 1986.
                <br /><br />Jacques Derrida, Of Grammatology, 16th pr (Johns Hopkins Univ. Pr, 1995).
                <br /><br />‘Positions - Andrés Jaque - Rearticulating the Social’, E-Flux, geraadpleegd 18 oktober 2025, https://www.e-flux.com/architecture/positions/280206/rearticulating-the-social.
                <br /><br />Lefebvre en Nicholson-Smith, The Production of Space.
            </div>
        </div>





      </div>

      
    </div>
  );
}

export default Archipel;
